import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Attach token from localStorage to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth
export const authApi = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  requestMagicLink: (data) => api.post('/auth/magic-link/request', data),
  verifyMagicLink: (data) => api.post('/auth/magic-link/verify', data)
};

// Users
export const usersApi = {
  getAll: () => api.get('/users'),
  getById: (id) => api.get(`/users/${id}`)
};

// Clients
export const clientsApi = {
  getAll: () => api.get('/clients'),
  getById: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`)
};

// Contacts
export const contactsApi = {
  getAll: (clientId) => api.get('/contacts', { params: { client_id: clientId } }),
  create: (data) => api.post('/contacts', data)
};

// Matters
export const mattersApi = {
  getAll: (clientId) => api.get('/matters', { params: { client_id: clientId } }),
  getById: (id) => api.get(`/matters/${id}`),
  create: (data) => api.post('/matters', data),
  update: (id, data) => api.put(`/matters/${id}`, data)
};

// Tasks
export const tasksApi = {
  getAll: (params) => api.get('/tasks', { params }),
  getById: (id) => api.get(`/tasks/${id}`),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  delete: (id) => api.delete(`/tasks/${id}`),
  getCharts: () => api.get('/tasks/charts'),
  getCalendar: (start, end) => api.get('/tasks/calendar', { params: { start, end } }),
  claimRecurring: (templateId, occurrenceDate) => api.post(`/tasks/claim-recurring?template_id=${templateId}&occurrence_date=${occurrenceDate}`)
};

// Projects
export const projectsApi = {
  getAll: (params) => api.get('/projects', { params }),
  getById: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`)
};

// OKRs
export const okrsApi = {
  getAll: (params) => api.get('/okrs', { params }),
  getById: (id) => api.get(`/okrs/${id}`),
  create: (data) => api.post('/okrs', data),
  update: (id, data) => api.put(`/okrs/${id}`, data),
  delete: (id) => api.delete(`/okrs/${id}`)
};

// Team
export const teamApi = {
  getAll: () => api.get('/team'),
  getById: (userId) => api.get(`/team/${userId}`),
  update: (userId, data) => api.put(`/team/${userId}`, data)
};

// Capacity
export const capacityApi = {
  getOverview: () => api.get('/capacity')
};

// Knowledge
export const knowledgeApi = {
  getAll: (params) => api.get('/knowledge', { params }),
  getById: (id) => api.get(`/knowledge/${id}`),
  create: (data) => api.post('/knowledge', data),
  upload: (file, category) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/knowledge/upload?category=${category}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

// Google Calendar
export const calendarApi = {
  getLoginUrl: () => api.get('/oauth/calendar/login'),
  getStatus: () => api.get('/calendar/status'),
  getEvents: (maxResults) => api.get('/calendar/events', { params: { max_results: maxResults } }),
  createEvent: (data) => api.post('/calendar/events', data),
  deleteEvent: (id) => api.delete(`/calendar/events/${id}`),
  disconnect: () => api.post('/calendar/disconnect')
};

// Leads
export const leadsApi = {
  getAll: (stage) => api.get('/leads', { params: { stage } }),
  getById: (id) => api.get(`/leads/${id}`),
  create: (data) => api.post('/leads', data),
  update: (id, data) => api.put(`/leads/${id}`, data),
  delete: (id) => api.delete(`/leads/${id}`),
  import: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/leads/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getActivities: (leadId) => api.get(`/leads/${leadId}/activities`),
  createActivity: (leadId, data) => api.post(`/leads/${leadId}/activities`, data),
  updateActivity: (leadId, activityId, data) => api.put(`/leads/${leadId}/activities/${activityId}`, data),
  deleteActivity: (leadId, activityId) => api.delete(`/leads/${leadId}/activities/${activityId}`)
};

// Comments
export const commentsApi = {
  getAll: (entityType, entityId) => api.get('/comments', { params: { entity_type: entityType, entity_id: entityId } }),
  create: (data) => api.post('/comments', data),
  delete: (id) => api.delete(`/comments/${id}`)
};

// Activity Log
export const activityApi = {
  getAll: (entityType, entityId) => api.get(`/activity/${entityType}/${entityId}`)
};

// Attachments
export const attachmentsApi = {
  getAll: (entityType, entityId) => api.get(`/attachments/${entityType}/${entityId}`),
  upload: (file, entityType, entityId) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/attachments/upload?entity_type=${entityType}&entity_id=${entityId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  download: (attachmentId) => `${API_URL}/api/attachments/download/${attachmentId}`,
  delete: (id) => api.delete(`/attachments/${id}`)
};

// AI
export const aiApi = {
  generate: (data) => api.post('/ai/generate', data),
  recommendAssignee: (params) => api.post('/ai/recommend-assignee', null, { params }),
  createDraft: (data) => api.post('/ai/draft', data),
  getDrafts: (params) => api.get('/ai/drafts', { params }),
  getDraft: (id) => api.get(`/ai/drafts/${id}`),
  updateDraft: (id, data) => api.put(`/ai/drafts/${id}`, null, { params: data }),
  deleteDraft: (id) => api.delete(`/ai/drafts/${id}`),
  sendDraft: (id) => api.post(`/ai/drafts/${id}/send`)
};

// Email
export const emailApi = {
  send: (data) => api.post('/email/send', data)
};

// Dashboard
export const dashboardApi = {
  getStats: () => api.get('/dashboard/stats')
};

// Notifications
export const notificationsApi = {
  getAll: (params) => api.get('/notifications', { params }),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
  checkDeadlines: () => api.get('/notifications/check-deadlines')
};

// BD AI Agent
export const bdAgentApi = {
  scoreLeads: () => api.post('/ai/lead-score'),
  getFollowups: () => api.post('/ai/lead-followups'),
  draftOutreach: (leadId, outreachType) => api.post(`/ai/lead-outreach?lead_id=${leadId}&outreach_type=${outreachType}`),
  getPipelineInsights: () => api.get('/ai/pipeline-insights')
};

// Courses
export const coursesApi = {
  getAll: (params) => api.get('/courses', { params }),
  getById: (id) => api.get(`/courses/${id}`),
  create: (data) => api.post('/courses', data),
  update: (id, data) => api.put(`/courses/${id}`, data),
  delete: (id) => api.delete(`/courses/${id}`),
  getAttachments: (courseId) => api.get(`/courses/${courseId}/attachments`),
  uploadAttachment: (courseId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/courses/${courseId}/attachments`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  downloadAttachment: (attachmentId) => `${API_URL}/api/courses/attachment/download/${attachmentId}`
};

export default api;
